import { Component, Host, h, Prop, State } from '@stencil/core';

@Component({
  tag: 'vds-teaser-flexible',
  styleUrl: 'vds-teaser-flexible.scss',
  shadow: true,
})
export class VdsTeaserFlexible {
  /**
   * Headline
   * Mandatory: yes
   */
  @Prop() headline = '';

  /**
   * Subline
   * Mandatory: yes
   */
  @Prop() subline = '';

  /**
   * Content
   * Mandatory: yes
   */
  @Prop() copy = '';

  /**
   * Caption
   * Mandatory: yes
   */
  @Prop() caption = '';

  /**
   * Copyright
   * Mandatory: yes
   */
  @Prop() copyright = '';

  /**
   * Event Start- / End-Date ISO String
   * mandatory: yes
   */
  @Prop() date: string;

  /**
   * Controls wether or not the share buttons are shown
   * in the actions panel.
   */
  @Prop() share?: string;

  /**
   * Controls wether or not the teaser flexible has
   * an actions panel.
   */
  @Prop() actions = true;

  /**
   * Controls wether or not the teaser flexible has
   * a read-more link.
   */
  @Prop() readMore = true;

  /**
   * Live event option (only for event teaser)
   */
  @Prop() live = false;

  /**
   * Visibility of the actions area
   */
  @State() private actionsVisible = false;

  render() {
    const { caption, copy, copyright, date, headline, live, subline, share, actions, readMore } = this;
    const parsedDate = this.parseStringToDate(date);

    return (
      <Host>
        <div class="relative overflow-hidden border-2 border-grey-lighter rounded-[5px]">
          <slot name="media"></slot>
          {live && this.renderLive()}
          <div class="relative">
            {parsedDate && this.renderDate(parsedDate)}
            <div class="relative box-border border-t-8 border-t-yellow bg-white">
              <div class="p-[30px] pb-[10px]">
                <span class="text-s3 text-grey-light uppercase">{subline}</span>
                <h3 class="font-heavy text-h3-mobile sm:text-h3-tablet lg:text-h3-desktop text-grey uppercase mt-[10px] mb-[20px] break-words" innerHTML={headline}></h3>
                <span class="block text-sidebar-mobile sm:text-sidebar-desktop text-grey">{copy}</span>
              </div>
              <div
                class={{
                  'p-[30px] pt-[10px]': true,
                  'pb-[30px] h-0': !actions && !copyright && !caption && !readMore
                }}>
                <p
                  class={{
                    'text-sm text-grey-light mb-[30px]': true,
                    'hidden': !caption && !copyright
                  }}>
                  {caption}
                  <span class="block pt-[10px]">{copyright}</span>
                </p>
                <div class="flex justify-end items-baseline">
                  <slot name="read-more"></slot>
                  {actions && this.renderAnimatedDots()}
                </div>
              </div>
              {actions && this.renderActions(share)}
            </div>
          </div>
        </div>
      </Host>
    );
  }

  private parseStringToDate(date: string): Date | null {
    const d = new Date(date);
    if (d instanceof Date && !isNaN(d.getTime())) {
      return d;
    }
    return null;
  }

  private renderDate(date: Date): string {
    return (
      <div class="top-[-35px] right-[15px] h-[60px] flex flex-col text-center bg-white rounded absolute text-white z-[1] text-[12px] justify-center items-center leading-[0]">
        <b class="flex items-center text-grey px-[5.5px] h-[35px] rounded-t uppercase font-black">
          {String(date.getDate()).padStart(2, '0')}. {this.getMonthAbbreviation(date)}
        </b>
        <b class="text-white bg-grey w-full h-[25px] leading-[22px] rounded-b uppercase font-black">{date.getFullYear()}</b>
      </div>
    );
  }

  private getMonthAbbreviation(date: Date): string {
    const monthName = date.toLocaleString(navigator.language, { month: 'long' });
    return monthName.length > 3 ? monthName.substring(0, 3) + '.' : monthName;
  }

  private renderAnimatedDots(): string {
    return (
      <button class="animated-dots float-right flex py-[15px] px-[10px] my-[-15px] mx-[-10px]" onClick={() => (this.actionsVisible = true)}>
        <div class="flex w-[6px] h-[6px] m-[2px] items-center content-center justify-center">
          <span class="dot-left block w-[4px] h-[4px] rounded-full bg-berry"></span>
        </div>
        <div class="flex w-[6px] h-[6px] m-[2px] items-center content-center justify-center">
          <span class="dot-middle block w-[4px] h-[4px] rounded-full bg-berry"></span>
        </div>
        <div class="flex w-[6px] h-[6px] m-[2px] items-center content-center justify-center">
          <span class="dot-right block w-[4px] h-[4px] rounded-full bg-berry"></span>
        </div>
      </button>
    );
  }

  private renderActions(shareUrl: string): string {
    return (
      <div
        class={{
          'action': true,
          'bottom-0': this.actionsVisible,
          'bottom-[-140%]': !this.actionsVisible,
        }}
      >
        <a class="absolute right-[13px] top-[-12px] w-[36px] h-[36px] bg-white text-grey rounded-[5px] shadow-[0_-14px_15px_-10px_rgba(0,0,0,0.15)] no-underline cursor-pointer">
          <div class="relative">
            <button class="absolute top-[8px] left-[8px] overflow-visible bg-transparent inline-block leading-[0]" onClick={() => (this.actionsVisible = false)}>
              <svg xmlns="http://www.w3.org/2000/svg" data-name="2px_Line_Icons" viewBox="51 51 26 26" width="19.5" height="19.5">
                <path d="M76 52 64 64" class="stroke-2 stroke-[grey] fill-[transparent]"></path>
                <path d="M76 76 52 52" class="stroke-2 stroke-[grey] fill-[transparent]"></path>
                <path d="M60 68 52 76" class="stroke-2 stroke-[grey] fill-[transparent]"></path>
              </svg>
            </button>
          </div>
        </a>
        <slot name="actions"></slot>
        {shareUrl && this.renderShareOptions(shareUrl)}
      </div>
    );
  }

  private renderShareOptions(url: string): string {
    return (
      <div class="px-[5px] mb-[10px]">
        <p class="mb-[5px]">Share on:</p>
        <div class="flex flex-wrap text-center justify-between">

          <a href={'https://www.facebook.com/sharer/sharer.php?u=' + url} target="_blank" class="text-grey scale-[0.8] transition duration-[250ms] ease-linear w-1/4 no-underline cursor-pointer">
            <span class="inline-block leading-[0] bg-transparent">
              <svg xmlns="http://www.w3.org/2000/svg" data-name="2px_Line_Icons" viewBox="53.94 53.94 20.12 20" width="38" height="38">
                <path d="M74.06,64a10.06,10.06,0,1,0-11.63,9.94v-7H59.87V64h2.56V61.78a3.55,3.55,0,0,1,3.8-3.91,15.18,15.18,0,0,1,2.25.2v2.47H67.21a1.46,1.46,0,0,0-1.64,1.57V64h2.79l-.44,2.91H65.57v7A10.07,10.07,0,0,0,74.06,64Z"></path>
              </svg>
            </span>
          </a>

          <a href={'https://twitter.com/intent/tweet?text=' + url} target="_blank" class="text-grey scale-[0.8] transition duration-[250ms] ease-linear w-1/4 no-underline cursor-pointer">
            <span class="inline-block leading-[0] bg-transparent">
              <svg xmlns="http://www.w3.org/2000/svg" data-name="2px_Line_Icons" viewBox="53.55 55.44 20.92 17.06" width="38" height="38">
                <path d="M60.12,72.5A12.13,12.13,0,0,0,72.33,60.29c0-.19,0-.37,0-.56a8.69,8.69,0,0,0,2.14-2.22,8.86,8.86,0,0,1-2.47.68,4.27,4.27,0,0,0,1.89-2.38,8.64,8.64,0,0,1-2.72,1,4.29,4.29,0,0,0-7.43,2.93,4.46,4.46,0,0,0,.11,1A12.19,12.19,0,0,1,55,56.29,4.3,4.3,0,0,0,56.33,62a4.4,4.4,0,0,1-1.95-.54v.05a4.29,4.29,0,0,0,3.44,4.21,4.21,4.21,0,0,1-1.93.07,4.28,4.28,0,0,0,4,3,8.57,8.57,0,0,1-5.34,1.84,8.72,8.72,0,0,1-1-.06,12.14,12.14,0,0,0,6.58,1.93"></path>
              </svg>
            </span>
          </a>

          <a href={'https://www.linkedin.com/sharing/share-offsite/?url=' +  url} target="_blank" class="text-grey scale-[0.8] transition duration-[250ms] ease-linear w-1/4 no-underline cursor-pointer">
            <span class="inline-block leading-[0] bg-transparent">
              <svg xmlns="http://www.w3.org/2000/svg" data-name="2px_Line_Icons" viewBox="54 54 20 20" width="38" height="38">
                <path d="M71,71h-3V66.4c0-1.11,0-2.53-1.54-2.53s-1.78,1.2-1.78,2.45V71h-3V61.5h2.85v1.3h0a3.13,3.13,0,0,1,2.81-1.54c3,0,3.55,2,3.55,4.55ZM58.45,60.19a1.72,1.72,0,1,1,1.72-1.72,1.72,1.72,0,0,1-1.72,1.72h0M59.93,71H57V61.5h3ZM72.52,54h-17A1.46,1.46,0,0,0,54,55.44V72.56A1.46,1.46,0,0,0,55.48,74h17A1.46,1.46,0,0,0,74,72.56V55.44A1.46,1.46,0,0,0,72.52,54"></path>
              </svg>
            </span>
          </a>

        </div>
      </div>
    );
  }

  private renderLive(): string {
    return (
      <div class="absolute flex leading-[0] text-white bg-berry justify-center items-center text-sm top-[15px] right-[15px] h-[18px] px-[8px] pb-[3px] z-[1] rounded">
        <b class="uppercase font-bold">Live</b>
      </div>
    );
  }
}
